/* eslint-disable camelcase */
import axios, { AxiosError, AxiosRequestConfig } from "axios";
import http from "http";
import https from "https";
import getConfig from "next/config";
import mockArticle from "mocks/MockArticle.json";
import {
    JobSearchRequest, JobSearchResults, UserLocation
} from "@themuse/design-system/lib/main";

import { isBrowser } from "utils/browser";
import { get, MetadataObj } from "./objects";
import { formatLog } from "./logging";
import { jobSearchQueryMaxlength } from "./constants";

export interface CompanyValue {
    object: "company_name_suggestion",
    // eslint-disable-next-line camelcase
    short_name: string
}

export interface CompanySuggestion {
    object: "suggestion",
    type: "company_name",
    text: string
    value: CompanyValue,
    image: string
}

export interface CompanySuggestorResults {
    object: "suggestions",
    data: CompanySuggestion[]
}

interface FilterArgs {
    [key: string]: string | number | string[];
}

export interface ApiArticleBreadcrumbLink {
    title: string;
    uri: string;
}

export interface ApiArticleBodyContent {
    html: string;
    type: string;
}

export interface ApiArticleCompanyReference {
    id: number;
    legacyId: string;
    primaryTopic: string;
    slug: string;
    status: string;
    tags: string;
    title: string;
    uri: string;
    url: string;
}

export interface ApiArticleCompanySponsor {
    id: number;
    description: string;
    legacyId: string;
    logoImage: string;
    primaryImage: string;
    secondaryImage: string;
    shortName: string;
    slug: string;
    status: string;
    tertiaryImage: string;
    title: string;
}

export interface ApiArticleCompanySponsorJob {
    id: number;
    name: string;
    short_name: string;
    level_displays: string[];
    location_values: string[];
    company_logo_image: string;
    company_name: string;
    company_short_name: string;
    company_id: number;
    apply_link: string;
    external_apply_link: string;
    model_type: string;
    relevance_score: number;
}

export interface ApiArticleImageAsset {
    altText: string;
    caption: string | null;
    credit: string | null;
    creditLink: string | null;
    height: number;
    url: string;
    width: number;
}

export interface ApiArticleCmsEntity {
    id: number;
    legacyId : string| null;
    primaryTopic: string | null;
    slug: string | null;
    status: string | null;
    tags: any;
    title: string | null;
    uri: string | null;
    url: string | null;
}

export interface ApiArticleSeo {
    MetaTitleContainer: string;
    MetaTagContainer: string;
    MetaLinkContainer: string;
    MetaScriptContainer: string;
    MetaJsonLdContainer: string;
}

export interface ApiArticleWriter {
    id: number;
    dateUpdated: string;
    email: string;
    enabled: boolean;
    isCoach: boolean;
    legacyId: string | null;
    linkedinProfile: string | null;
    longBiography: string | null;
    slug: string
    twitterHandle: string | null;
    uri: string;
    website: string | null;
    writerName: string;
    writerTitle: string | null;
    biography: string | null;
    headshot: ApiArticleImageAsset[];
}

export interface ApiArticleMeta {
    status: string;
    section: string;
    type: string;
    api: {
        version: string;
    },
    lastUpdated: string;
    location?: string;
    statusCode?: number;
}

// ApiArticle reflects the data type that is returned from the API,
// which is distinct from the data we actually pass to the frontend.
export interface ApiArticle {
    additionalTopics: ApiArticleCmsEntity[];
    articleBodyContent: ApiArticleBodyContent[];
    articleContentType: string;
    availableForSyndication: boolean;
    breadcrumbLinks: ApiArticleBreadcrumbLink[];
    careerTypes: string[];
    companiesReferenced: ApiArticleCompanyReference[];
    sponsoredJobs: ApiArticleCompanySponsorJob[];
    companySponsor: ApiArticleCompanySponsor;
    companySponsorExpiration: string | null;
    contentEditor: any; // Unknown format
    dateCreated: string;
    dateUpdated: string;
    enabled: boolean;
    excerpt: string | null;
    id: number;
    includeInRecents: boolean;
    jobLevel: any[]; // Unknown format, possibly unused
    jwPlayerVideo: string;
    leadMediaCaption: string | null;
    leadMediaPhoto: ApiArticleImageAsset;
    leadMediaVideo: any; // Unknown format, possibly unused
    legacyId: string | null;
    legacyTags: ApiArticleCmsEntity[];
    location: any[]; // Unknown format, possibly unused
    postDate: string;
    primaryTopic: ApiArticleCmsEntity;
    product: ApiArticleCmsEntity;
    seo: ApiArticleSeo;
    slug: string;
    sponsorCredit: string | null;
    sponsorDescription: string | null;
    sponsorLink: string | null;
    tags: ApiArticleCmsEntity[];
    templateName?: string;
    title: string;
    uri: string;
    wasMigrated: boolean;
    writerAltText: string | null;
    writer: ApiArticleWriter;
    meta: ApiArticleMeta;
}

export interface NewsletterSubmissionValues {
    email: string
}

// Max length for job-search suggestion api input
export const SUGGESTION_INPUT_MAX_LENGTH = 70;

/* eslint-disable-next-line max-len */
export const fetchJobRecommendations = async (axiosOptions?: AxiosRequestConfig) => {
    const { publicRuntimeConfig: { apiRequestTimeoutMs, apiBase } } = getConfig();
    const response = await axios.get(`${apiBase}/recommendations`, { ...axiosOptions, ...{ timeout: apiRequestTimeoutMs } });
    return (response as any).data;
};

/**
 * Request the user.
 */
export const fetchUser = async (axiosOptions?: AxiosRequestConfig) => {
    const { publicRuntimeConfig: { apiRequestTimeoutMs, appName, apiBase } } = getConfig();
    const config: AxiosRequestConfig = {
        ...{
            timeout: apiRequestTimeoutMs,
            headers: {
                "Content-Type": "application/json",
            }
        },
        ...axiosOptions
    };
    const path = `${apiBase}/user`;
    try {
        const response = await axios(path, config);
        return response.data;
    } catch (error) {
        console.error(`${appName} was unable to request the user from ${path}.`, JSON.stringify(formatLog({ error })));
        return null;
    }
};

/**
 * Request user from the service API.
 */
export const fetchApiUser = async (oskarId = "", sessionId = "", axiosOptions?: AxiosRequestConfig) => {
    const {
        publicRuntimeConfig: {
            apiRequestTimeoutMs, museApiHost, museHost, appName
        }
    } = getConfig();
    const config: AxiosRequestConfig = {
        ...{
            timeout: apiRequestTimeoutMs,
            headers: {
                "Content-Type": "application/json",
                cookie: `oskar_id=${oskarId}; session_id=${sessionId}`,
                referer: museHost
            }
        },
        ...axiosOptions
    };
    const path = `${museApiHost}/api/user`;
    try {
        const response = await axios(path, config);
        return response;
    } catch (error) {
        console.error(`${appName} was unable to request the user from ${path}.`, JSON.stringify(formatLog({ error })));
        return null;
    }
};

/**
 * newsletter optin from the public API.
 */
export const newsletterSubscribeRequest = async (values: NewsletterSubmissionValues, axiosOptions?: AxiosRequestConfig) => {
    const { publicRuntimeConfig: { apiRequestTimeoutMs, appName, apiBase } } = getConfig();

    const config: AxiosRequestConfig = {
        ...{
            timeout: apiRequestTimeoutMs,
            headers: {
                "Content-Type": "application/json",
            }
        },
        ...axiosOptions
    };
    const path = `${apiBase}/newsletter/subscribe`;

    try {
        const response = await axios.post(path, values, config);
        if (response.status === 200 || response.status === 201) {
            return true;
        }
        throw new Error(`${appName}: Sendgrid newsletter subscription was unsuccessful. Status: ${response.status}`);
    } catch (error) {
        console.error(JSON.stringify(formatLog({ error })));
        return false;
    }
};

/**
 * newsletter opt in from the service API.
 */
export const newsletterApiSubscribeRequest = async (email: string, axiosOptions?: AxiosRequestConfig) => {
    const {
        publicRuntimeConfig: {
            apiRequestTimeoutMs, museApiHost, appName, museHost
        },
        serverRuntimeConfig: { emailAlertToken }
    } = getConfig();

    const emailSubscription = {
        email,
        page_section: "advice"
    };

    const config = {
        ...{
            timeout: apiRequestTimeoutMs,
            headers: {
                Authorization: `Shared-Token token=${emailAlertToken}`,
                Origin: museHost,
            }
        },
        ...axiosOptions
    };

    const path = `${museApiHost}/api/newsletter/subscribe_with_token`;

    try {
        const response = await axios.post(path, emailSubscription, config);
        return response.data;
    } catch (error) {
        console.error(`${appName}: newsletter API request to ${path} failed.`, JSON.stringify(formatLog({ error })));
        return null;
    }
};

/**
 * @param  {string} companyName  - Company Name
 * @param  {number} page         - Page interval to retrieve
 * @param  {number} itemsPerPage - Items returned per page
 */
export const getJobsForCompany = async (companyName: string, page: number, itemsPerPage?: number) => {
    const { serverRuntimeConfig: { apiRequestTimeoutMs, jobsApiHost }, publicRuntimeConfig: { appName } } = getConfig();
    if (!companyName) {
        return { data: [] };
    }
    const company = encodeURIComponent(companyName);
    const config: AxiosRequestConfig = { timeout: apiRequestTimeoutMs };
    const apiPath = "/v1/jobs/search-sponsored-jobs-module";
    const query = `items_per_page=${itemsPerPage}&page=${page}&company=${company}`;
    const path = `${jobsApiHost}${apiPath}?${query}`;
    try {
        const response = await axios(path, config);
        return {
            data: get(response, "data.results")
        };
    } catch (error) {
        console.error(`${appName} was unable to request company jobs from ${path}`, JSON.stringify(formatLog({ error })));
        return null;
    }
};

/**
 * @param  {FilterArgs} filterArgs - Args for filtering posts
 */
export const getOptimizedPosts = async (filterArgs: FilterArgs) => {
    const { serverRuntimeConfig: { apiRequestTimeoutMs, articlesApiHost, mockApiResponses }, publicRuntimeConfig: { appName } } = getConfig();
    const { uri, token, preview } = filterArgs;
    const config: AxiosRequestConfig = { timeout: apiRequestTimeoutMs };
    const query = token && preview ? `?x-craft-live-preview=${preview}&token=${token}` : "";
    const path = `${articlesApiHost}/api/${uri}${query}`;

    if (mockApiResponses) {
        const response = mockArticle;
        return {
            data: get(response, "data.data"),
            meta: get(response, "data.metadata")
        };
    }
    let response: MetadataObj;
    try {
        response = await axios.get(path, config);
    } catch (error) {
        const msg = `${appName} was unable to request posts from ${path}`;
        console.error(msg, JSON.stringify(formatLog({ error })));
    }

    return {
        data: get(response, "data.data"),
        meta: get(response, "data.metadata")
    };
};

/**
 * Request company suggestions from the service API.
 */
export const fetchApiCompanySuggestions = async (query: string): Promise<CompanySuggestorResults> => {
    const { serverRuntimeConfig: { apiRequestTimeoutMs, jobsApiHost }, publicRuntimeConfig: { appName } } = getConfig();
    // if query exists we will pass it in the params.
    const queryObj = query ? { query } : {};
    const config: AxiosRequestConfig = { params: { type: "company_name", ...queryObj }, timeout: apiRequestTimeoutMs };
    const path = `${jobsApiHost}/api/v2/suggestions`;
    try {
        const response = await axios(path, config);
        return response.data;
    } catch (error) {
        console.error(`${appName} was unable to request jobs from ${path}`, JSON.stringify(formatLog({ error })));
        return null;
    }
};

/**
 * Request company suggestions.
 */
export const fetchCompanySuggestions = async (inputValue: string, axiosOptions?: AxiosRequestConfig) => {
    const { publicRuntimeConfig: { apiRequestTimeoutMs, appName, apiBase } } = getConfig();
    const path = `${apiBase}/company-suggestion/${inputValue}`;
    try {
        const response = await axios(path, { ...axiosOptions, ...{ timeout: apiRequestTimeoutMs } });
        return response.data;
    } catch (error) {
        console.error(`${appName} was unable to request company suggestions from ${path}`, JSON.stringify(formatLog({ error })));
        return null;
    }
};

/**
 * Get data from Carmen using requesters ip address by default
 * @param ip optional IP address to lookup
 */

export const fetchApiCarmenData = async (ip?: string, axiosOptions?: AxiosRequestConfig): Promise<UserLocation> => {
    const { publicRuntimeConfig: { apiRequestTimeoutMs, appName, carmenApiHost } } = getConfig();
    const carmenPath = ip ? `/ip/${ip}` : "/me";
    const path = `${carmenApiHost}${carmenPath}`;
    try {
        const response = await axios(path, { ...axiosOptions, ...{ timeout: apiRequestTimeoutMs } });
        return response.data;
    } catch (error) {
        console.error(`${appName} was unable to request carmen location from ${path}`, JSON.stringify(formatLog({ error })));
        return null;
    }
};

export const searchJobs = async (queryParams: JobSearchRequest, cursor: string = "") => {
    const { serverRuntimeConfig: { jobsApiHost } } = getConfig();
    const { publicRuntimeConfig: { apiBase, apiRequestTimeoutMs } } = getConfig();
    const config: AxiosRequestConfig = {
        timeout: apiRequestTimeoutMs,
        params: {
            start_after: cursor || null,
            ...queryParams,
            ...(queryParams.query && { query: queryParams.query.substring(0, jobSearchQueryMaxlength) })
        }
    };

    const path = isBrowser()
        ? `${apiBase}/job-search`
        : `${jobsApiHost}/api/v2/search/jobs`;

    const httpAgentConfig = {
        keepAlive: true,
        timeout: 5000,
        maxSockets: Infinity
    };

    const jobRendererAxiosClient = axios.create({
        timeout: 5000,
        httpAgent: new http.Agent(httpAgentConfig),
        httpsAgent: new https.Agent(httpAgentConfig),
        maxRedirects: 10,
        maxContentLength: 50 * 1000 * 1000
    });

    const result = await jobRendererAxiosClient.get(path, config)
        .catch((error: AxiosError) => {
            const msg = `Error requesting the job search at ${path}.`;
            const err = JSON.stringify(formatLog({ error }));
            console.error(msg, err);
        })
        .then((res: any) => res?.data);
    return result as JobSearchResults;
};
